import { Box, Button, Chip, Typography } from '@mui/material';
import Loading from 'components/common/Loading';
import FormikAutosuggest from 'components/formik/Autosuggest';
import Checkbox from 'components/formik/Checkbox';
import TextField from 'components/formik/TextField';
import { FastField, Field, Form } from 'formik';
import { arrayOf, bool, number, object, oneOf } from 'prop-types';
import React, { useEffect } from 'react';
import { I18n } from 'utils/i18n';
import employeeShape from 'shapes/employee';
import teamShape from 'shapes/team';
import { useLazyListTeamsQuery } from 'store/app/entities/TeamsSlice';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';
import connector from './connector';
import formik from './formik';
import ActionButtons from './formik/ActionButtons';
import EmployeesTable from './formik/EmployeesTable';
import Headline from './Headline';
import Label from './Label';

const TeamForm = ({
  team = null,
  teamId,
  errors,
  isEdit,
  actions,
  loading,
  values,
  isSuperAdmin,
  onDelete,
  onClose,
  activeSection = 'details',
}) => {
  const [triggerListTeamsQuery] = useLazyListTeamsQuery();
  const section = activeSection || 'all';

  useEffect(() => {
    if (isEdit) {
      actions.teams.find(teamId);
    }
  }, [actions.teams, teamId, isEdit]);

  const handleReset = () => {
    if (onClose) onClose();
    else actions.drawers.team.close();
  };

  if (isEdit && !team?.users) {
    return <Loading />;
  }

  const scrollAreaSx =
    section === 'members'
      ? {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: '20px',
        }
      : {
          flex: 1,
          overflow: 'auto',
          p: '20px',
        };

  return (
    <Form
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={scrollAreaSx}>
        {(section === 'details' || section === 'all') && (
          <>
            <Headline isEdit={isEdit} />
            <Box
              sx={theme => ({
                display: 'flex',
                [theme.breakpoints.down('sm')]: {
                  flexWrap: 'wrap',
                },
                pb: '30px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& > div': {
                  width: '100%',
                },
              })}
            >
              <div>
                <Label>{I18n.t('TeamDrawer.NameField.label')}</Label>
                <FastField
                  component={TextField}
                  autoComplete="off"
                  placeholder={I18n.t('TeamDrawer.NameField.placeholder')}
                  name="name"
                  fullWidth
                />
                <Label>{I18n.t('TeamDrawer.DescriptionField.label')}</Label>
                <FastField
                  component={TextField}
                  autoComplete="off"
                  placeholder={I18n.t('TeamDrawer.DescriptionField.placeholder')}
                  name="description"
                />
                <Label>{I18n.t('TeamDrawer.ParentField.label')}</Label>
                <FastField
                  component={FormikAutosuggest}
                  loadOptions={loadOptionsLazyQuery}
                  additional={{ page: 1, exclude: values.teams, trigger: triggerListTeamsQuery }}
                  placeholder={I18n.t('TeamDrawer.ParentField.placeholder')}
                  name="parent"
                  isClearable
                />
                <Label>{I18n.t('TeamDrawer.DefaultRoleField.label')}</Label>
                <FastField
                  component={FormikAutosuggest}
                  suggestions={['member', 'admin', 'guest'].map(value => ({
                    label: I18n.t(`Roles.${value}`),
                    value,
                  }))}
                  placeholder={I18n.t('TeamDrawer.DefaultRoleField.placeholder')}
                  name="defaultRole"
                />
                {team && (
                  <div>
                    <Label>{I18n.t('TeamDrawer.Chats')}</Label>
                    {team.groups?.map(c => (
                      <Chip
                        label={c.name + (c.description ? ` - ${c.description}` : '')}
                        key={c.name}
                      />
                    ))}
                    <Typography variant="body2">
                      {team.groups?.length ? null : I18n.t('TeamDrawer.No Chats')}
                    </Typography>
                  </div>
                )}
              </div>
            </Box>
          </>
        )}
        {(section === 'members' || section === 'all') &&
          (loading ? (
            <Loading size={56} />
          ) : (
            <>
              <Field component={EmployeesTable} defaultRole={values.defaultRole} name="members" />
              {(isSuperAdmin || !values.isReadOnly) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifySelf: 'flex-start',
                    flexShrink: 0,
                    color: 'text.secondary',
                  }}
                >
                  <FastField
                    disabled={values.optOutDisabled}
                    component={Checkbox}
                    label={I18n.t('TeamDrawer.Opt out from this team')}
                    name="isOptedOut"
                  />
                </Box>
              )}
            </>
          ))}
      </Box>
      <Box
        sx={theme => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        })}
      >
        {onDelete ? (
          <Button color="error" onClick={onDelete} disabled={loading}>
            {I18n.t('TeamsSettings.Delete team')}
          </Button>
        ) : (
          <div />
        )}
        {errors.server && (
          <Typography color="error" sx={{ padding: '0 20px', alignSelf: 'center' }}>
            {errors.server}
          </Typography>
        )}
        <ActionButtons onReset={handleReset} isEdit={isEdit} disabled={loading} />
      </Box>
    </Form>
  );
};

TeamForm.propTypes = {
  teamId: number.isRequired,
  team: teamShape,
  actions: object.isRequired,
  isEdit: bool.isRequired,
  employees: arrayOf(employeeShape).isRequired,
  errors: object.isRequired,
  loading: bool.isRequired,
  values: object.isRequired,
  isSuperAdmin: bool.isRequired,
  activeSection: oneOf(['details', 'members', 'all']),
};

export default connector(formik(TeamForm));
